import type { FaceBenefits, PlanBenefits } from '../types/planBenefits';
import type { ControlUpsellOffer } from '../constants/controlUpsellOffers';
import { CONTROL_UPSELL_OFFERS } from '../constants/controlUpsellOffers';
import type { PromoCampaign } from '../lib/promoCampaign/types';

export interface FaceBenefitsResult {
  /** Se a face possui benefícios definidos */
  hasBenefits: boolean;
  /** Texto de GB de Bônus (ex: "4GB de Bônus", "70GB + 1GB de Bônus") ou null */
  gbText: string | null;
  /** Parte de quantidade do bônus, sem o sufixo " de Bônus" (ex: "70GB + 1GB") ou null */
  gbAmount: string | null;
  /** Raw gbCore value when benefits are visible, undefined when hidden */
  gbCore?: number;
  /** Raw gbInternet value when benefits are visible, undefined when hidden */
  gbInternet?: number;
  /** Texto de validade (ex: "Oferta válida por 30 dias") ou null */
  validityText: string | null;
  /** Se algum benefício deve ser exibido */
  showBenefits: boolean;
}

export function formatGbValue(gbCore: number): string {
  // Formatação manual (sem toLocaleString): ambientes sem ICU pt-BR
  // (ex: node do runner alpine no CI) caem em fallback en-US e trocam a vírgula por ponto
  const rounded = Math.round(gbCore * 10) / 10;
  return String(rounded).replace('.', ',');
}

/**
 * Monta a parte de quantidade do bônus de GB, combinando GB Core e Bônus de
 * Internet (solicitação da operadora).
 *
 * Regras:
 * - Só Bônus de Internet → "XGB"
 * - GB Core + Bônus de Internet → "XGB + YGB" (Core primeiro, Internet depois)
 * - Só GB Core → "XGB"
 * - Nenhum dos dois positivo → null
 */
export function formatGbAmount(
  gbCore: number | undefined,
  gbInternet: number | undefined,
): string | null {
  const core = isPositive(gbCore) ? `${formatGbValue(gbCore)}GB` : null;
  const internet = isPositive(gbInternet)
    ? `${formatGbValue(gbInternet)}GB`
    : null;

  if (core && internet) return `${core} + ${internet}`;
  return core ?? internet;
}

function formatValidity(days: number): string {
  const label = days === 1 ? 'dia' : 'dias';
  return `Oferta válida por ${days} ${label}`;
}

function isPositive(value: number | undefined): value is number {
  return typeof value === 'number' && value > 0;
}

export function calcFaceBenefits(
  planBenefits: PlanBenefits | null | undefined,
  faceId: number,
  showBonusGlobal: boolean = true,
): FaceBenefitsResult {
  // Sem planBenefits ou sem faces → sem benefícios
  if (!planBenefits?.faces) {
    return emptyResult();
  }

  // Face precisa existir em faces
  const faceKey = String(faceId);
  const face = planBenefits.faces[faceKey];

  if (!face) {
    return emptyResult();
  }

  // showBonus=false global ou por face → oculta tudo
  const shouldShow = showBonusGlobal && face.showBonus !== false;
  if (!shouldShow) {
    return {
      hasBenefits: true,
      gbText: null,
      gbAmount: null,
      validityText: null,
      showBenefits: false,
    };
  }

  const gbAmount = formatGbAmount(face.gbCore, face.gbInternet);
  const hasInternetBonus = isPositive(face.gbInternet);
  const gbText =
    gbAmount !== null
      ? hasInternetBonus
        ? `${gbAmount} de Bônus`
        : gbAmount
      : null;
  const gbCoreVal = isPositive(face.gbCore) ? face.gbCore : undefined;
  const gbInternetVal = isPositive(face.gbInternet)
    ? face.gbInternet
    : undefined;
  const validityText = isPositive(face.validityDays)
    ? formatValidity(face.validityDays)
    : null;
  const hasBenefits = gbText !== null || validityText !== null;

  return {
    hasBenefits,
    gbText,
    gbAmount,
    gbCore: gbCoreVal,
    gbInternet: gbInternetVal,
    validityText,
    showBenefits: true,
  };
}

/**
 * Na campanha o bônus de internet sai da tela (vai na etiqueta): gbText e
 * gbAmount passam a trazer só o GB core. Sem campanha devolve os benefícios
 * como vieram.
 */
export function applyPromoToBenefits(
  benefits: FaceBenefitsResult,
  promo: PromoCampaign | null | undefined,
): FaceBenefitsResult {
  if (!promo) return benefits;
  const gbCore = formatGbAmount(benefits.gbCore, undefined);
  return {
    ...benefits,
    gbText: gbCore,
    gbAmount: gbCore,
    gbInternet: undefined,
  };
}

/** Resultado do cálculo de Upsell de Face */
export interface UpsellOfferResult {
  /** Produto destino do upsell */
  targetProduct: { id: number; productId?: string };
  /** Benefícios da face destino */
  targetFace: FaceBenefits;
}

/**
 * Calcula se há Upsell de Face disponível para a face selecionada.
 *
 * Condições:
 * - planBenefits existe
 * - Face selecionada existe em planBenefits.faces
 * - upsell > 0
 * - Produto destino upsell existe em rechargeValues
 *
 * @returns UpsellOfferResult com produto destino e face destino, ou null
 */
export function calcUpsellOffer(
  planBenefits: PlanBenefits | null | undefined,
  selectedFaceId: number,
  rechargeValues: Array<{ id: number; productId?: string }>,
): UpsellOfferResult | null {
  if (!planBenefits?.faces) return null;

  const faceKey = String(selectedFaceId);
  const face = planBenefits.faces[faceKey];

  if (!face) return null;
  if (!isPositive(face.upsell)) return null;

  const targetProduct = rechargeValues.find(v => v.id === face.upsell);
  if (!targetProduct) return null;

  const targetFaceKey = String(face.upsell);
  const targetFace = planBenefits.faces[targetFaceKey] ?? {};

  return { targetProduct, targetFace };
}

/** Produto com categoria da API (runtime, tipo não captura todos os campos) */
interface RechargeValueWithCategory {
  id: number;
  productId?: string;
  category?: string;
}

/** Resultado do cálculo de Recarga Premiada */
export interface RewardedRechargeResult {
  /** Produto de Recarga Premiada */
  targetProduct: { id: number; productId?: string };
  /** Valor do produto premiado em centavos */
  value: number;
}

/** Categoria que identifica produto de Recarga Premiada no backend */
const PREMIADA_CATEGORY = 'MICRO_RELOAD';

/**
 * Calcula se há Recarga Premiada disponível.
 *
 * Procura em rechargeValues por produto com category === 'MICRO_RELOAD'.
 * Retorna null se nenhum encontrado — sem hardcode de ID.
 *
 * @returns RewardedRechargeResult com produto e valor, ou null
 */
export function calcRewardedRecharge(
  planBenefits: PlanBenefits | null | undefined,
  rechargeValues: Array<{ id: number; productId?: string }>,
  checkoutEnabled?: boolean,
): RewardedRechargeResult | null {
  if (!planBenefits?.rewardedRecharge) return null;
  if (checkoutEnabled === false) return null;

  const values = rechargeValues as RechargeValueWithCategory[];

  // Busca por category === 'MICRO_RELOAD' (produto de Recarga Premiada)
  const productByCategory = values.find(v => v.category === PREMIADA_CATEGORY);
  if (productByCategory) {
    return { targetProduct: productByCategory, value: productByCategory.id };
  }

  return null;
}

/** Resultado do cálculo de Upsell de Controle */
export interface ControlUpsellOfferResult {
  offer: ControlUpsellOffer;
}

/** Valores de recarga elegíveis ao Upsell de Controle (R$30 e R$40, em centavos) */
const CONTROL_UPSELL_VALUES = [3000, 4000];

/**
 * Calcula se há Upsell de Controle disponível.
 *
 * Condições (todas obrigatórias):
 * - checkout ativo no canal
 * - flag de sessão do fluxo de upsell de controle (auth_code)
 * - trait topup_campaign do CDP === 'upsell_hybrid' (grantlist)
 * - valor selecionado é R$30 ou R$40
 * - existe oferta configurada (CONTROL_UPSELL_OFFERS) para o valor
 */
export function calcControlUpsellOffer(
  productId: number,
  checkoutEnabled: boolean | undefined,
  upsellPlan: boolean | undefined,
  isTopupCampaignEligible: boolean | undefined,
): ControlUpsellOfferResult | null {
  if (!checkoutEnabled) return null;
  if (!upsellPlan || !isTopupCampaignEligible) return null;
  if (!CONTROL_UPSELL_VALUES.includes(productId)) return null;

  const offer = CONTROL_UPSELL_OFFERS[productId];
  if (!offer) return null;

  return { offer };
}

/** Ações possíveis após selecionar uma Face de Recarga */
export type SelectValueAction =
  | { type: 'controlUpsell'; controlUpsellResult: ControlUpsellOfferResult }
  | { type: 'upsell'; upsellResult: UpsellOfferResult }
  | { type: 'premiada'; premiadaResult: RewardedRechargeResult }
  | {
      type: 'combined';
      upsellResult: UpsellOfferResult;
      premiadaResult: RewardedRechargeResult;
    }
  | { type: 'continue' };

/**
 * Decide que ação tomar quando uma Face de Recarga é selecionada.
 *
 * Ordem de avaliação:
 * 1. Upsell de Controle (sessão + trait CDP) — prioridade total, não combina com os demais
 * 2. Upsell de Face (planBenefits-driven)
 * 3. Recarga Premiada (planBenefits-driven) — rewardedRecharge
 * 4. Continue (nenhuma oferta disponível)
 */
export function decideValueFlow(
  planBenefits: PlanBenefits | null | undefined,
  productId: number,
  rechargeValues: Array<{ id: number; productId?: string }>,
  checkoutEnabled?: boolean,
  upsellPlan?: boolean,
  isTopupCampaignEligible?: boolean,
): SelectValueAction {
  // 1. Upsell de Controle — prioridade total sobre upsell de face e premiada
  const controlUpsellResult = calcControlUpsellOffer(
    productId,
    checkoutEnabled,
    upsellPlan,
    isTopupCampaignEligible,
  );

  if (controlUpsellResult) {
    return { type: 'controlUpsell', controlUpsellResult };
  }

  // 2. Upsell de Face (planBenefits-driven) — avalia antes de qualquer gate
  const upsellResult = calcUpsellOffer(planBenefits, productId, rechargeValues);

  // 3. Recarga Premiada (planBenefits-driven) — rewardedRecharge
  // Gate: só disponível quando smartCheckout.checkout está ativo
  const premiadaResult = calcRewardedRecharge(
    planBenefits,
    rechargeValues,
    checkoutEnabled,
  );

  if (upsellResult && premiadaResult) {
    return { type: 'combined', upsellResult, premiadaResult };
  }

  if (upsellResult) {
    return { type: 'upsell', upsellResult };
  }

  if (premiadaResult) {
    return { type: 'premiada', premiadaResult };
  }

  return { type: 'continue' };
}

export function extractValidityValue(
  validityText: string | null,
): string | null {
  if (!validityText) return null;
  return validityText.replace('Oferta válida por ', '');
}

/**
 * Monta o objeto extraBenefits para o payload do checkout.
 *
 * Usa planBenefits como fonte canônica.
 *
 * Regra:
 * - planBenefits existe e face tem showBenefits true → monta a partir da API
 * - planBenefits existe mas benefícios zerados → retorna vazio
 * - planBenefits ausente → retorna vazio
 */
export function calcCheckoutExtraBenefits(
  planBenefits: PlanBenefits | null | undefined,
  faceId: number,
  promo: PromoCampaign | null = null,
): Record<string, string> {
  const benefits = applyPromoToBenefits(
    calcFaceBenefits(planBenefits, faceId, true),
    promo,
  );

  if (!benefits.showBenefits) {
    return promo ? { Bônus: promo.checkoutBonus } : {};
  }

  const extraBenefits: Record<string, string> = {};
  const validityValue = extractValidityValue(benefits.validityText);

  if (validityValue) {
    extraBenefits.Validade = validityValue;
  }

  // Na campanha o bônus de internet dá lugar ao da campanha, cada um com a sua
  // validade (ex: "8,6GB válido por 30 dias + 10 GB válido por 7 dias")
  const bonusParts: string[] = [];
  if (benefits.gbAmount) {
    const validitySuffix = validityValue ? ` válido por ${validityValue}` : '';
    bonusParts.push(`${benefits.gbAmount}${validitySuffix}`);
  }
  if (promo) bonusParts.push(promo.checkoutBonus);
  if (bonusParts.length > 0) extraBenefits['Bônus'] = bonusParts.join(' + ');

  return extraBenefits;
}

/**
 * Ordena valores de recarga pela prioridade definida em planBenefits.faces.
 *
 * Faces sem prioridade definida vão para o final (priority 99).
 * Valores que não estão em faces são removidos do resultado.
 *
 * @returns Novo array ordenado por priority (1 = primeiro)
 */
export function sortRechargeValuesByPriority<T extends { id: number }>(
  faces: Record<string, FaceBenefits>,
  values: T[],
): T[] {
  return values
    .filter(v => faces[String(v.id)] !== undefined)
    .sort((a, b) => {
      const priorityA = faces[String(a.id)]?.priority ?? 99;
      const priorityB = faces[String(b.id)]?.priority ?? 99;
      return priorityA - priorityB;
    });
}

/**
 * Decide se o card de Recarga Premiada deve ser exibido na tela de sucesso.
 *
 * Usa planBenefits.planType da matriz PLAN_BENEFITS_MATRIX (fonte canônica),
 * removendo a dependência de traits (tim_pre_top).
 *
 * @returns true quando planType === 'timPre'
 */
export function calcShowRewardedRechargeCard(
  planBenefits: PlanBenefits | null | undefined,
): boolean {
  return planBenefits?.planType === 'timPre';
}

function emptyResult(): FaceBenefitsResult {
  return {
    hasBenefits: false,
    gbText: null,
    gbAmount: null,
    validityText: null,
    showBenefits: false,
  };
}
